/**
 * screens/ApproverQueue — Approver · "Waiting on you"
 * ──────────────────────────────────────────────────────────────────────────
 * Three KPI cards, then the queue.
 *
 * THE DUAL-APPROVAL DETAIL THAT MATTERS MOST HERE:
 * On an item needing two approvals, the button reads "Approve (1 of 2)" and the
 * rule column carries a chip saying "1 of 2 approvals · Department head +
 * Finance manager". Someone clicking a plain "Approve" on a dual item would
 * reasonably believe the money then moves. It does not — the item stays pending
 * until the second, DIFFERENT approver acts, and the button has to say so
 * before it is pressed, not afterwards in a toast.
 *
 * Approve is inline and instant. Reject opens the reason modal, because a
 * rejection must carry a reason the requester sees (invariant 5) — there is no
 * one-click reject anywhere in this product.
 *
 * "Value in the queue" is grouped PER CURRENCY, largest as the value and the
 * rest on the sub line. Invariant 4: no blended total, anywhere.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import {
  Button,
  Card,
  DataTable,
  EmptyPanel,
  Grid,
  KpiCard,
  Loading,
  Money,
  PageContainer,
  PageHeader,
  StatusPill,
  type Column,
} from '../../components';
import { kpiAccent, neutral } from '../../theme/colors';
import { card as cardSpace } from '../../theme/spacing';
import { sans, size as fontSize } from '../../theme/typography';
import { ellipsisStyle } from '../../theme/webStyle';
import { useAsyncData } from '../../lib/useAsyncData';
import { dateTime } from '../../lib/dates';
import { dotted } from '../../lib/format';
import { format, formatRest, splitTotals } from '../../lib/money';
import {
  decide,
  decideBulk,
  fetchApprovals,
  fetchQueueSummary,
} from '../../services/approvals/approvals.service';
import { handleApiError } from '../../utils/errorHandler';
import { showCompleted, showSuccess } from '../../utils/successHandler';
import { toastWarn } from '../../components/Toast';
import { approveLabel, awaitsSecondApproval, type Approval, type QueueSummary } from '../../schemas/approvals';
import { useShell } from '../../context/ShellContext';

export function ApproverQueueScreen() {
  const { dataVersion, invalidate, openModal } = useShell();
  const [deciding, setDeciding] = useState<string | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);

  const load = useCallback(
    async (): Promise<{ queue: Approval[]; summary: QueueSummary | null }> => {
      const [queue, summary] = await Promise.all([
        fetchApprovals('pending'),
        fetchQueueSummary().catch(() => null),
      ]);
      return { queue, summary };
    },
    [],
  );

  const { data, loading } = useAsyncData(load, [], {
    version: dataVersion,
    errorTitle: 'Could not load your queue',
  });

  const queue = data?.queue ?? [];
  const summary = data?.summary ?? null;

  const value = useMemo(() => splitTotals(summary?.value ?? []), [summary]);

  /** Approve one item, inline. */
  const approve = async (approval: Approval) => {
    setDeciding(approval.id);
    try {
      const result = await decide(approval.id, 'approve');
      // "Released" is the fact that matters, and it is NOT the same as
      // "approved": on a dual item the first approval moves nothing.
      if (result.released) {
        showCompleted(result, 'Approved and released', 'The spend has been routed.');
      } else {
        showSuccess(
          result,
          'Approved',
          `${result.approved_by.length} of ${result.needs} approvals. A second, different approver is still required.`,
        );
      }
      invalidate();
    } catch (error) {
      handleApiError(error, 'Approve failed');
    } finally {
      setDeciding(null);
    }
  };

  const approveAll = async () => {
    setBulkRunning(true);
    try {
      const result = await decideBulk(
        queue.map((row) => row.id),
        'approve',
      );
      showCompleted(
        result,
        'Queue processed',
        [
          `${result.released.length} released`,
          result.still_pending.length > 0
            ? `${result.still_pending.length} awaiting a second approval`
            : null,
        ]
          .filter(Boolean)
          .join(' · '),
      );
      for (const skipped of result.skipped) {
        toastWarn('One item was not approved', skipped.error);
      }
      invalidate();
    } catch (error) {
      handleApiError(error, 'Approve all failed');
    } finally {
      setBulkRunning(false);
    }
  };

  const columns = useMemo<Column<Approval>[]>(
    () => [
      {
        key: 'request',
        header: 'Request',
        track: { fr: 1.9 },
        render: (row) => (
          <View style={styles.cellStack}>
            <Text numberOfLines={1} style={styles.label}>
              {row.label ?? row.kind}
            </Text>
            <Text numberOfLines={1} style={styles.meta}>
              {dotted(row.who, dateTime(row.created_at))}
            </Text>
          </View>
        ),
      },
      {
        key: 'amount',
        header: 'Amount',
        track: { fr: 1 },
        align: 'right',
        render: (row) =>
          row.amount_minor && row.cur ? (
            <Money value={row.amount_minor} cur={row.cur} size={fontSize.sm} align="right" />
          ) : (
            <Text style={styles.meta}>—</Text>
          ),
      },
      {
        key: 'rule',
        header: 'Rule that caught it',
        track: { fr: 1.7 },
        render: (row) => (
          <View style={styles.cellStack}>
            <Text numberOfLines={2} style={styles.meta}>
              {row.rule_summary ?? '—'}
            </Text>
            {/* The dual-approval chip: who else must act, and how far along. */}
            {row.needs > 1 && (
              <View style={styles.dualChip}>
                <StatusPill
                  label={`${row.progress} · ${row.approvers.join(' + ')}`}
                  tone="pending"
                  capitalize={false}
                  dense
                />
              </View>
            )}
          </View>
        ),
      },
      {
        key: 'actions',
        header: '',
        track: { px: 178 },
        align: 'right',
        stackedLabel: '',
        render: (row) => (
          <View style={styles.actions}>
            <Button
              label="Reject"
              size="md"
              variant="danger"
              onPress={() => openModal({ kind: 'reject', approvalId: row.id })}
            />
            <Button
              // "Approve (1 of 2)" on a dual item awaiting its first decision.
              label={approveLabel(row)}
              size="md"
              variant="primary"
              loading={deciding === row.id}
              onPress={() => void approve(row)}
            />
          </View>
        ),
      },
    ],
    // The render callbacks close over `deciding` and `approve`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deciding, openModal],
  );

  if (loading && !data) {
    return (
      <PageContainer width="approver">
        <Loading />
      </PageContainer>
    );
  }

  const overThreshold = queue.filter((row) => !awaitsSecondApproval(row)).length;

  return (
    <PageContainer width="approver" testID="screen-queue">
      <PageHeader
        title="Waiting on you"
        subtitle={`${queue.length} over the threshold. A rejection must say why.`}
        right={
          queue.length > 0 ? (
            <Button
              label="Approve all"
              size="lg"
              variant="outline"
              loading={bulkRunning}
              onPress={approveAll}
            />
          ) : undefined
        }
      />

      <View style={styles.kpis}>
        <Grid min={220} gap={cardSpace.gridGap}>
          <KpiCard
            label="Waiting on you"
            accent={kpiAccent.pendingApprovals}
            tone="pending"
            value={String(summary?.pending ?? queue.length)}
            sub={overThreshold > 0 ? `${overThreshold} ready to release` : undefined}
          />
          <KpiCard
            label="Value in the queue"
            accent={kpiAccent.cashInCustody}
            value={value.largest ? format(value.largest.total_minor, value.largest.cur) : '—'}
            sub={value.rest.length > 0 ? formatRest(value.rest) : undefined}
          />
          <KpiCard
            label="Decided this month"
            accent={kpiAccent.unreconciled}
            tone="positive"
            value={String(summary?.decided_this_month ?? 0)}
          />
        </Grid>
      </View>

      {queue.length === 0 ? (
        <EmptyPanel title="Queue clear" hint="Nothing has crossed the threshold." />
      ) : (
        <Card>
          <DataTable
            columns={columns}
            rows={queue}
            keyExtractor={(row) => row.id}
            density="roomy"
            columnGap={16}
          />
        </Card>
      )}
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  kpis: { marginBottom: 22 },
  cellStack: { minWidth: 0, gap: 1 },
  label: { ...sans('500'), fontSize: fontSize.body, color: neutral.ink, ...ellipsisStyle },
  meta: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
  dualChip: { marginTop: 5, alignSelf: 'flex-start' },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 6 },
});

export default ApproverQueueScreen;
